import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Tag, Play, Headphones, BookOpen, Radio, Video, LayoutGrid } from "lucide-react";
import Navigation from "@/components/layout/Navigation";
import Footer from "@/components/layout/Footer";
import { supabase } from "@/integrations/supabase/client";
import { detectMediaType, type MediaType } from "@/lib/media-utils";

interface Post {
  id: string;
  title: string;
  slug: string | null;
  excerpt: string | null;
  content: string | null;
  image_url: string | null;
  video_url: string | null;
  audio_url: string | null;
  category: string | null;
  published_at: string | null;
}

const FieldNotes = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [mediaFilter, setMediaFilter] = useState<"all" | "read" | "listen" | "watch">("all");

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const { data, error } = await supabase
        .from("posts")
        .select("*")
        .not("published_at", "is", null)
        .order("published_at", { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const categories = [...new Set(posts.map((p) => p.category).filter(Boolean))];
  
  const mediaFilteredPosts = posts.filter((p) => {
    if (mediaFilter === "all") return true;
    const type = detectMediaType(p.video_url, p.audio_url);
    if (mediaFilter === "watch") return type === "video";
    if (mediaFilter === "listen") return type === "audio";
    if (mediaFilter === "read") return type === "text";
    return true;
  });

  const filteredPosts = selectedCategory ? mediaFilteredPosts.filter((p) => p.category === selectedCategory) : mediaFilteredPosts;

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  const getExcerpt = (post: Post, maxLength = 150) => {
    if (post.excerpt) return post.excerpt;
    if (!post.content) return "";
    const stripped = post.content.replace(/<[^>]*>/g, "");
    return stripped.length > maxLength ? stripped.substring(0, maxLength) + "..." : stripped;
  };

  const getPostUrl = (post: Post) => `/field-notes/${post.slug || post.id}`;

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="pt-24 pb-16 paper-texture">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h1 className="font-display text-4xl md:text-5xl font-bold text-primary mb-4">Field Notes & Mishaps</h1>
            <p className="text-lg text-foreground">Stories from the road. Lessons from the disasters. Advice you can actually use.</p>
          </div>

          {/* Media type filter */}
          <div className="flex justify-center gap-2 mb-6">
            {([
              { key: "all", label: "All", icon: LayoutGrid },
              { key: "read", label: "Read", icon: BookOpen },
              { key: "listen", label: "Listen", icon: Headphones },
              { key: "watch", label: "Watch", icon: Video },
            ] as const).map(({ key, label, icon: Icon }) => (
              <button key={key} onClick={() => setMediaFilter(key)}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-mono uppercase tracking-wider transition-colors ${mediaFilter === key ? "bg-accent text-accent-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </div>

          {categories.length > 0 && (
            <div className="flex flex-wrap justify-center gap-2 mb-12">
              <button onClick={() => setSelectedCategory(null)}
                className={`px-4 py-2 rounded-full text-sm font-mono uppercase tracking-wider transition-colors ${selectedCategory === null ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
                All Categories
              </button>
              {categories.map((category) => (
                <button key={category} onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-mono uppercase tracking-wider transition-colors ${selectedCategory === category ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
                  {category}
                </button>
              ))}
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-12"><p className="text-muted-foreground">Loading field notes...</p></div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">No field notes yet.</p>
              <p className="text-sm text-muted-foreground">Check back soon for stories from the road!</p>
            </div>
          ) : (
            <div className="masonry-grid max-w-6xl mx-auto">
              {filteredPosts.map((post, index) => {
                const mediaType = detectMediaType(post.video_url, post.audio_url);
                return (
                  <Link key={post.id} to={getPostUrl(post)} className="masonry-item block">
                    <article className="polaroid hover:shadow-xl transition-all duration-300 hover:-translate-y-1 animate-fade-in"
                      style={{ animationDelay: `${index * 0.1}s`, transform: `rotate(${index % 2 === 0 ? -2 : 2}deg)` }}>
                      {/* Image with media overlay */}
                      <div className="relative">
                        {post.image_url ? (
                          <img src={post.image_url} alt={post.title} className="w-full aspect-[4/3] object-cover" loading="lazy" />
                        ) : (
                          <div className="w-full aspect-[4/3] bg-muted flex items-center justify-center">
                            <span className="font-mono text-muted-foreground text-sm">No image</span>
                          </div>
                        )}
                        {mediaType === "video" && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-14 h-14 rounded-full bg-accent/70 flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-110">
                              <Play className="w-7 h-7 text-accent-foreground ml-0.5" />
                            </div>
                          </div>
                        )}
                        {mediaType === "audio" && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-14 h-14 rounded-full bg-secondary/70 flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-110">
                              <Headphones className="w-7 h-7 text-secondary-foreground" />
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="p-3">
                        <h2 className="font-display text-lg font-bold text-primary mb-3 leading-snug line-clamp-2">{post.title}</h2>
                        <p className="text-sm text-foreground mb-3 line-clamp-3">{getExcerpt(post)}</p>
                        <div className="flex items-center justify-between text-[11px] text-muted-foreground font-mono">
                          <span>{mediaType === "video" ? "▶ Watch" : mediaType === "audio" ? "🎧 Listen" : "📖 Read"}</span>
                          <span>{post.published_at ? formatDate(post.published_at) : ""}</span>
                        </div>
                      </div>
                    </article>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default FieldNotes;